/**
 * MapPlot — Deckorate "Map Plot" feature (AIVA-5434).
 *
 * Step-by-step wizard:
 *
 *   1. Map Region        — pick the map; its projection places the markers.
 *   2. Insert Map Slide  — insert the chosen map as a new editable slide.
 *   3. Shade Regions     — name states/countries → Resolve → Apply Region Fill
 *                          colours those shapes on the live slide.
 *   4. Locations         — list places → Resolve Locations checks they geocode.
 *   5. Add markers       — plot markers on the inserted slide. Repeatable: add
 *                          more locations to the SAME slide as often as needed.
 *
 * NONE of the map *logic* lives here — this file only wires buttons to the
 * feature's `services/` (`insertMapSlide`, `fillRegionsMultiColor`,
 * `plotMarkers`, `buildMapSlide`, projection + geocoding helpers). See
 * `services/mapPlotter.ts` / `services/projectionEngine.ts` for how placement
 * (and the per-map projection corrections) actually work.
 *
 * The heavy SVG→OOXML builder (pptxgenjs + jszip) is dynamically imported only
 * when inserting, so it stays out of the initial bundle.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { ModuleHeader } from "../../../../../shared/components/ModuleHeader";
import { logError, logEvent, scrubMessage } from "../../../../../telemetry";
import { getGeocoder } from "../services/geocoding";
import {
  fetchMapSvg,
  getMapList,
  getProfile,
  MAP_REGISTRY,
} from "../services/mapRegistry";
import { getRegionNamesFromSvg } from "../services/svgPathParser";
import {
  fillRegionsMultiColor,
  getMapBounds,
  insertMapSlide,
  isMapPlottingSupported,
  plotMarkers,
} from "../services/mapPlotter";
import type { MapLocation, MarkerConfig, MarkerShape } from "../types";
import styles from "./MapPlot.module.css";

/** connectorSystemName tag for all Deckorate map telemetry. */
const MAP_CONNECTOR = "deckorate";

/** Report a map failure through the shared telemetry pipeline (PII-scrubbed). */
function logMapError(reason: string, type: string, message: string): void {
  logError(
    "error.connector.failure",
    { type, scrubbedMessage: scrubMessage(message) },
    { connectorSystemName: MAP_CONNECTOR, reason },
  );
}

interface MapPlotProps {
  readonly onBack?: () => void;
}

interface ResolvedLocation extends MapLocation {
  raw: string;
  resolved: boolean;
}

/** A shade-region line resolved to an SVG shape name (or null = unmatched). */
interface ResolvedRegion {
  raw: string;
  shapeName: string | null;
}

const MARKER_SHAPES: ReadonlyArray<{ value: MarkerShape; label: string }> = [
  { value: "ellipse", label: "● Dot" },
  { value: "diamond", label: "◆ Diamond" },
  { value: "triangle", label: "▲ Triangle" },
  { value: "rectangle", label: "■ Square" },
];

const PRESET_COLORS = [
  "1F6F8B",
  "E07B2E",
  "1E6B2F",
  "2E8BC0",
  "9B3FA8",
  "5BA13A",
  "111111",
  "1B2A4A",
];

const REGION_OPTIONS = getMapList();

const toHashHex = (h: string): string => `#${h}`;
const stripHash = (h: string): string => h.replace(/^#/, "").toUpperCase();

export function MapPlot({ onBack }: Readonly<MapPlotProps>) {
  const supported = useMemo(() => isMapPlottingSupported(), []);

  const [regionId, setRegionId] = useState("world");
  /** The region actually inserted on the slide (fill + markers use this). */
  const [generatedRegion, setGeneratedRegion] = useState<string | null>(null);

  const [regionsText, setRegionsText] = useState("");
  const [regionColor, setRegionColor] = useState(PRESET_COLORS[0]!);
  const [resolvedRegions, setResolvedRegions] = useState<ResolvedRegion[]>([]);

  const [locationsText, setLocationsText] = useState("");
  const [resolved, setResolved] = useState<ResolvedLocation[]>([]);

  const [marker, setMarker] = useState<MarkerConfig>({
    shape: "ellipse",
    size: 12,
    color: "D13438",
    borderColor: "FFFFFF",
    borderWidth: 0.75,
    label: true,
  });

  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const begin = (): void => {
    setError(null);
    setStatus(null);
    setBusy(true);
  };

  /* ── Step 1: insert the (unshaded) map slide ── */

  const insertMap = useCallback(async () => {
    begin();
    try {
      // Always use the explicitly chosen region — the projection that places
      // markers must match the map the user picked.
      const region = regionId;
      const entry = MAP_REGISTRY[region];
      if (!entry) throw new Error(`Unknown map region: ${region}`);

      setStatus(`Building ${entry.name} map…`);
      const svg = await fetchMapSvg(region);
      const { buildMapSlide } = await import("../services/mapSlideBuilder");
      const base64 = await buildMapSlide(svg, entry.viewBox, {});
      await insertMapSlide(base64);

      setRegionId(region);
      setGeneratedRegion(region);
      setResolvedRegions([]);
      setStatus(
        `✓ ${entry.name} map slide inserted. Shade regions or add locations below.`,
      );
      logEvent("connector.action.invoke", "usage", {
        connectorSystemName: MAP_CONNECTOR,
        reason: "map-insert",
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Insert failed";
      setError(msg);
      logMapError("map-insert", "MapInsertError", msg);
    } finally {
      setBusy(false);
    }
  }, [regionId]);

  /* ── Step 2a: resolve shade-region names → SVG shape names ── */

  const resolveRegions = useCallback(async () => {
    begin();
    try {
      const region = generatedRegion ?? regionId;
      const names = regionsText
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => l.length > 0);
      if (names.length === 0) {
        setResolvedRegions([]);
        setStatus("Enter one region per line, then Resolve.");
        return;
      }
      const svg = await fetchMapSvg(region);
      const shapeNames = getRegionNamesFromSvg(svg);
      const lower = new Map(shapeNames.map((n) => [n.toLowerCase(), n]));
      const geocoder = region === "world" ? await getGeocoder() : null;

      const out: ResolvedRegion[] = names.map((raw) => {
        let target = lower.get(raw.toLowerCase());
        if (!target && geocoder) {
          const iso = geocoder.resolveToCanonicalCountry(raw);
          if (iso) target = lower.get(iso.toLowerCase());
        }
        return { raw, shapeName: target ?? null };
      });
      setResolvedRegions(out);
      const matched = out.filter((r) => r.shapeName).length;
      setStatus(
        `Resolved ${matched}/${out.length} region${out.length !== 1 ? "s" : ""}.`,
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Resolve regions failed";
      setError(msg);
    } finally {
      setBusy(false);
    }
  }, [generatedRegion, regionId, regionsText]);

  /* ── Step 2b: apply the fill to the resolved regions on the live slide ── */

  const applyRegionFill = useCallback(async () => {
    begin();
    try {
      if (!generatedRegion) throw new Error("Insert the map slide first.");
      const matched = resolvedRegions.filter((r) => r.shapeName);
      if (matched.length === 0) {
        throw new Error("No resolved regions — click Resolve Region(s) first.");
      }
      const colorMap: Record<string, string> = {};
      for (const r of matched) colorMap[r.shapeName!] = regionColor;
      const filled = await fillRegionsMultiColor(colorMap);
      setStatus(`Filled ${filled} region${filled !== 1 ? "s" : ""}.`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Apply fill failed";
      setError(msg);
      logMapError("map-fill", "MapFillError", msg);
    } finally {
      setBusy(false);
    }
  }, [generatedRegion, resolvedRegions, regionColor]);

  /* ── Step 3a: resolve location lines → lat/lon ── */

  const resolveLocationsInner = async (
    text: string,
  ): Promise<ResolvedLocation[]> => {
    const geocoder = await getGeocoder();
    const lines = text
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    return lines.map((raw) => {
      const hit =
        geocoder.getByExactName(raw) ?? geocoder.search(raw, { limit: 1 })[0];
      if (hit)
        return { raw, name: hit.name, lat: hit.lat, lon: hit.lon, resolved: true };
      return { raw, name: raw, lat: 0, lon: 0, resolved: false };
    });
  };

  const resolveLocations = useCallback(async (): Promise<ResolvedLocation[]> => {
    const out = await resolveLocationsInner(locationsText);
    setResolved(out);
    return out;
  }, [locationsText]);

  useEffect(() => {
    resolveLocations().catch(() => undefined);
  }, []);

  /* ── Step 3b: plot markers (repeatable) ── */

  const addMarkers = useCallback(async () => {
    begin();
    try {
      const region = generatedRegion;
      if (!region) throw new Error("Insert the map slide first.");
      const locs = await resolveLocations();
      const markers: MapLocation[] = locs
        .filter((l) => l.resolved)
        .map(({ name, lat, lon }) => ({ name, lat, lon }));
      if (markers.length === 0) throw new Error("No resolved locations to add.");

      const bounds = await getMapBounds();
      if (!bounds) {
        throw new Error(
          "No map on the active slide — select the inserted map slide first.",
        );
      }

      // Guard against region/slide desync: the fitted map box's aspect equals
      // the region's viewBox aspect. If the live box doesn't match the region
      // we're projecting for, we'd plot into the ocean — stop and say so.
      const vb = MAP_REGISTRY[region]!.viewBox;
      const expected = vb.height / vb.width;
      const actual = bounds.height / bounds.width;
      if (Math.abs(expected - actual) > 0.05) {
        throw new Error(
          `Active slide doesn't match the ${MAP_REGISTRY[region]!.name} map ` +
            `(box aspect ${actual.toFixed(2)} vs expected ${expected.toFixed(2)}). ` +
            `Select the map slide you just inserted, then add markers.`,
        );
      }

      const result = await plotMarkers(
        markers,
        marker,
        bounds,
        getProfile(region),
        null,
      );
      let msg = `Added ${result.placed} marker${result.placed !== 1 ? "s" : ""}.`;
      if (result.skipped.length > 0) {
        msg += ` Off-map: ${result.skipped.map((s) => s.name).join(", ")}.`;
      }
      setStatus(msg);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Add markers failed";
      setError(msg);
      logMapError("map-markers", "MapMarkersError", msg);
    } finally {
      setBusy(false);
    }
  }, [generatedRegion, resolveLocations, marker]);

  const validCount = resolved.filter((l) => l.resolved).length;

  /* ── Render ── */

  return (
    <div className={styles.panel}>
      <ModuleHeader title="Map Plot" subtitle="Deckorate" onBack={onBack} />
      <div className={styles.body}>
        {!supported && (
          <p className={styles.notice}>
            Map plotting needs PowerPoint (desktop or web) with PowerPointApi 1.4+.
          </p>
        )}

        {/* 1 — Map region + insert */}
        <label className={styles.label} htmlFor="map-region">
          Map Region
        </label>
        <select
          id="map-region"
          className={styles.select}
          value={regionId}
          onChange={(e) => setRegionId(e.target.value)}
        >
          {REGION_OPTIONS.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
        <p className={styles.hint}>
          Markers are placed using the projection for the map you choose here.
          Select a map and click Insert to add it as a new slide.
        </p>
        <button
          type="button"
          className={styles.btnPrimary}
          data-cmd-action="map-insert-slide"
          onClick={() => {
            insertMap().catch(() => undefined);
          }}
          disabled={busy}
        >
          <span className={styles.btnIcon} aria-hidden="true">
            🗺
          </span>{" "}
          {busy ? "Working…" : "Insert Map Slide"}
        </button>

        {/* 2 — Shade regions → resolve → apply fill */}
        <label className={styles.label} htmlFor="map-regions">
          Shade Regions
        </label>
        <textarea
          id="map-regions"
          className={styles.textArea}
          rows={3}
          value={regionsText}
          onChange={(e) => setRegionsText(e.target.value)}
          placeholder={
            (generatedRegion ?? regionId) === "us"
              ? "Enter state names, one per line:\nCalifornia\nTexas"
              : "Enter country names, one per line:\nAustralia\nBrazil"
          }
        />
        <button
          type="button"
          className={styles.btnSecondary}
          data-cmd-action="map-resolve-regions"
          onClick={() => {
            resolveRegions().catch(() => undefined);
          }}
          disabled={busy}
        >
          Resolve Region(s)
        </button>
        {resolvedRegions.length > 0 && (
          <ul className={styles.hint} style={{ margin: "4px 0", paddingLeft: 16 }}>
            {resolvedRegions.map((r) => (
              <li key={r.raw} style={{ color: r.shapeName ? undefined : "#a4262c" }}>
                {r.shapeName ? `${r.raw} → ${r.shapeName}` : `${r.raw} — not found`}
              </li>
            ))}
          </ul>
        )}
        <fieldset className={styles.fieldset}>
          <legend className={styles.label}>Default Fill Color</legend>
          <div className={styles.chips}>
            {PRESET_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={`#${c}`}
                aria-pressed={regionColor === c}
                onClick={() => setRegionColor(c)}
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  border:
                    regionColor === c
                      ? "2px solid var(--wl-blue)"
                      : "1px solid #ccc",
                  background: toHashHex(c),
                  cursor: "pointer",
                }}
              />
            ))}
            <input
              type="color"
              aria-label="Custom fill colour"
              value={toHashHex(regionColor)}
              onChange={(e) => setRegionColor(stripHash(e.target.value))}
              style={{ width: 30, height: 26 }}
            />
          </div>
        </fieldset>
        <button
          type="button"
          className={styles.btnSecondary}
          data-cmd-action="map-apply-region-fill"
          onClick={() => {
            applyRegionFill().catch(() => undefined);
          }}
          disabled={busy || !generatedRegion}
        >
          Apply Region Fill
        </button>

        {/* 3 — Locations → resolve → markers */}
        <label className={styles.label} htmlFor="map-locations">
          Locations
        </label>
        <textarea
          id="map-locations"
          className={styles.textArea}
          rows={4}
          value={locationsText}
          onChange={(e) => setLocationsText(e.target.value)}
          placeholder={"London\nTokyo\n40.7, -74.0"}
        />
        <p className={styles.hint}>
          City names, country names, or lat/long coordinates. One per line.
        </p>
        <button
          type="button"
          className={styles.btnSecondary}
          data-cmd-action="map-resolve-locations"
          onClick={() => {
            resolveLocations().catch(() => undefined);
          }}
          disabled={busy}
        >
          Resolve Locations
        </button>
        {resolved.length > 0 && (
          <ul className={styles.hint} style={{ margin: "4px 0", paddingLeft: 16 }}>
            {resolved.map((l) => (
              <li key={l.raw} style={{ color: l.resolved ? undefined : "#a4262c" }}>
                {l.resolved
                  ? `${l.name} (${l.lat.toFixed(2)}, ${l.lon.toFixed(2)})`
                  : `${l.raw} — not found`}
              </li>
            ))}
          </ul>
        )}

        <fieldset className={styles.fieldset}>
          <legend className={styles.label}>Marker Style</legend>
          <div className={styles.chips}>
            {MARKER_SHAPES.map((s) => (
              <button
                key={s.value}
                type="button"
                className={`${styles.chip} ${marker.shape === s.value ? styles.chipActive : ""}`}
                onClick={() => setMarker((m) => ({ ...m, shape: s.value }))}
              >
                {s.label}
              </button>
            ))}
          </div>
        </fieldset>
        <div className={styles.row}>
          <input
            type="color"
            aria-label="Marker colour"
            value={toHashHex(marker.color)}
            onChange={(e) =>
              setMarker((m) => ({ ...m, color: stripHash(e.target.value) }))
            }
          />
          <input
            type="number"
            className={styles.input}
            aria-label="Marker size (pt)"
            min={2}
            max={48}
            value={marker.size}
            onChange={(e) =>
              setMarker((m) => ({ ...m, size: Number(e.target.value) || m.size }))
            }
            style={{ width: 72 }}
          />
          <label>
            <input
              type="checkbox"
              checked={marker.label}
              onChange={(e) =>
                setMarker((m) => ({ ...m, label: e.target.checked }))
              }
            />{" "}
            Labels
          </label>
        </div>
        <button
          type="button"
          className={styles.btnPrimary}
          data-cmd-action="map-add-markers"
          onClick={() => {
            addMarkers().catch(() => undefined);
          }}
          disabled={busy || !generatedRegion || validCount === 0}
        >
          Add {validCount} marker{validCount !== 1 ? "s" : ""} →
        </button>
        {!generatedRegion && (
          <p className={styles.hint}>
            Insert the map slide first, then add markers (repeatable).
          </p>
        )}

        {status && <p className={styles.notice}>{status}</p>}
        {error && <p className={styles.error}>{error}</p>}
      </div>
    </div>
  );
}
